import { computed, effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { QueryClient } from '@tanstack/angular-query-experimental';

import { ProfileService } from './profile/profile.service';

/**
 * Service to handle invalidation of queries when exhaustive mode changes.
 * Just add the `invalidateOnExhaustiveMode: true` meta field to any query options like this:
 * ```typescript
 * queryOptions({
 *   queryKey: ['annotations', 'byProject', projectId()],
 *   queryFn: () => {
 *    lastValueFrom(this.flywheel.annotations.get({
 *      filter: `parents.project=${projectId()}`,
 *      sort: 'modified:desc',
 *      exhaustive: this.exhaustiveMode(),
 *    })),
 *   },
 *   placeholderData: [],
 *   meta: { invalidateOnExhaustiveMode: true },
 * });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ExhaustiveModeService {
  private profileService = inject(ProfileService);
  private queryClient = inject(QueryClient);

  private siteSettings = toSignal(this.profileService.settings$);

  exhaustiveModeEnabled = computed(() => {
    return this.siteSettings().showAllData === true;
  });

  invalidateQueriesOnExhaustiveToggle = effect(() => {
    this.exhaustiveModeEnabled();
    this.queryClient.invalidateQueries({
      predicate: query => query.meta?.invalidateOnExhaustiveMode === true,
    });
  });

}
